import {cleanupSource, determineAmountOfTimeItTakesForAudioToStart, getAudioContext} from "../audioContext";
import accurateTimingWorker from "../accurateTimingWorker";
//@ts-ignore
import {PitchShifter} from 'soundtouchjs';
import deviceInfo from "../deviceInfo/deviceInfo";
import {logger} from "../log/logger";

export default class AudioPlayerSoundTouch {
  url: string;
  seekTime: number = 0;
  audioContextStartTime: number = 0;
  playbackRate: number = 1;
  //what our source node should be connected to before being played. (reverb, filter, etc)
  destination: AudioNode;
  gainNode: GainNode;
  volume: number = 1;
  isPlaying: boolean = false;
  shifter: PitchShifter;
  audioBuffer: AudioBuffer;
  source: AudioBufferSourceNode;
  shouldUseShifter: boolean = false;
  canDeviceDoRealtimePitchShifting: boolean = deviceInfo.canDoRealtimePitchShifting();
  msItTakesForSoundTouchToStartPlaying: number;//soundtouch doesn't immediately start playing, so we determine how long it takes and factor that in to our play time.
  loadPromise: Promise<AudioPlayerSoundTouch>;
  loadResolve: (audioPlayer: AudioPlayerSoundTouch) => void;
  loadReject: (error: any) => void;
  isLoadingOrAlreadyLoaded: boolean = false;
  abortController: AbortController;
  constructor(url: string) {
    this.url = url;
    this.destination = getAudioContext().destination;
    this.gainNode = getAudioContext().createGain();
    this.gainNode.connect(this.destination);
    deviceInfo.canDoRealtimePitchShifting();
    this.abortController = new AbortController();
    this.loadPromise = new Promise<AudioPlayerSoundTouch>((resolve, reject)=>{
      this.loadResolve = resolve;
      this.loadReject = reject;
    });
  }

  async load() {
    if(this.isLoadingOrAlreadyLoaded){ return this.loadPromise; }
    this.isLoadingOrAlreadyLoaded = true;
    try{
      this.audioBuffer = await fetchAndDecodeAudioData({url: this.url, context: getAudioContext(), signal: this.abortController.signal});
      this.loadResolve(this);
    }catch(e){
      this.loadReject(e);
      throw e;
    }
    return this.loadPromise;
  }

  async cancelLoad(){
    if(this.audioBuffer){ return; }
    logger.lessonAudio.warn({message: `cancelLoad for: ${this.url}`});
    // this.abortController.abort(); //https://bugs.webkit.org/show_bug.cgi?id=215771 safari sucks so this is what is probably causing the white screen.
  }

  async play() {
    this.gainNode.gain.value = this.volume;
    cleanupSource(this.source);
    await this.loadPromise;

    if (this.shouldUseShifter) {
      return this.playUsingShifter();
    } else {
      return this.playUsingWebAudio();
    }
  }

  private playUsingWebAudio() {
    //create a new audio source, as once start has been called, it cannot be called again.
    const source = createAudioSourceFromAudioBuffer(this.audioBuffer, getAudioContext());
    //connect to our gain, which should be connected to our destination (passed in by Synthesizer)
    source.connect(this.gainNode);
    this.source = source;
    source.start(0, this.seekTime);
    this.audioContextStartTime = performance.now() / 1000;
    this.isPlaying = true;
  }

  private async pretendToPlay(){
    this.audioContextStartTime = performance.now() / 1000;
    this.isPlaying = true;
  }

  /**
   * Initially we determined this right after count in finished, but we can make things seem faster by calling this function as soon as count in starts.
   */
  async determineAmountOfTimeItTakesForAudioToStart(){
    await this.loadPromise;
    if(this.msItTakesForSoundTouchToStartPlaying === undefined){
      this.msItTakesForSoundTouchToStartPlaying = await determineAmountOfTimeItTakesForAudioToStart(this.audioBuffer, getAudioContext(), this.playbackRate);
    }
  }

  private async playUsingShifter() {
    if(!this.canDeviceDoRealtimePitchShifting){ return this.pretendToPlay(); }
    if(this.shifter){ this.shifter.disconnect(); }
    this.shifter = new PitchShifter(getAudioContext(), this.audioBuffer);
    this.shifter.tempo = this.playbackRate;
    const duration = this.shifter?.duration ?? 1;
    const percentage = this.seekTime / duration;
    this.shifter.percentagePlayed = percentage;

    //we don't know when SoundTouch actually starts playing, so we connect a script processor node, analyze the audio data, and determine how long it takes for audio data to come through.
    if(this.msItTakesForSoundTouchToStartPlaying === undefined){
      this.msItTakesForSoundTouchToStartPlaying = await determineAmountOfTimeItTakesForAudioToStart(this.audioBuffer, getAudioContext(), this.playbackRate);
    }
    const promise = new Promise<void>((resolve)=>{
      accurateTimingWorker.setTimeout(()=>{
        this.audioContextStartTime = performance.now() / 1000;
        this.isPlaying = true;
        resolve();
      }, this.msItTakesForSoundTouchToStartPlaying);
    });

    this.shifter.connect(this.gainNode);
    return promise;
  }

  async pause() {
    if (this.shifter) {
      this.shifter.disconnect();
    }
    this.isPlaying = false;
    if (this.source) {
      try {
        this.source.stop();
      } catch (e) {
        logger.lessonAudio.error({message: `error stopping source.`});
      }
    }
  }

  async stop() {
    await this.pause();
    this.setCurrentTime(0);
    this.shifter.off("play");
  }

  setPlaybackRate(rate: number) {
    this.playbackRate = rate;
    const roundedRate = round(rate, 4); //sometimes the rate from SS can be 1.00000005
    this.shouldUseShifter = roundedRate !== 1;
  }

  setCurrentTime(time: number) {
    this.seekTime = time;
    const duration = this.shifter?.duration ?? 1;
    const percentage = this.seekTime / duration;
    if (this.isPlaying) {
      void this.pause();
      if(this.shifter){
        this.shifter.percentagePlayed = percentage;
      }
      void this.play();
    } else if (this.shifter) {
      this.shifter.percentagePlayed = percentage;
    }
  }

  setVolume(volume: number) {
    this.volume = volume;
    this.gainNode.gain.value = volume;
  }

  getCurrentTime() {
    const endTime = performance.now() / 1000;
    // const currentTime = (this.seekTime * this.playbackRate) + ((endTime - this.audioContextStartTime) * this.playbackRate);
    return this.seekTime + ((endTime - this.audioContextStartTime) * this.playbackRate);
  }
}

/**
 * Retrieves, decodes, and returns an audio data array buffer of the audio data found at the specified url.
 * @param url - url of mp3, wav, etc.
 * @param context - audio context used to decodeAudioData
 * @param signal - used to abort in flight requests.
 */
async function fetchAndDecodeAudioData({url, context, signal}: {url: string; context: AudioContext, signal: AbortSignal}): Promise<AudioBuffer> {
  const response = await fetch(url, {cache: "force-cache", signal});
  const arrayBuffer = await response.arrayBuffer();
  return decodeAudioData(context, arrayBuffer);
}

/**
 * Safari doesn't support the promise based decodeAudioData func, so provide our own.
 * https://developer.mozilla.org/en-US/docs/Web/API/BaseAudioContext/decodeAudioData
 * @param context
 * @param arrayBuffer
 */
async function decodeAudioData(context: AudioContext, arrayBuffer: ArrayBuffer): Promise<AudioBuffer>{
  return new Promise((resolve, reject) => context.decodeAudioData(arrayBuffer, resolve, reject));
}

/**
 * Convenience function to create an AudioBufferSourceNode, which is used to play/stop/etc an audio sample.
 * @param audioBuffer - decoded audio data representation of the audio sample
 * @param context - audio context used to create the AudioBufferSourceNode
 */
function createAudioSourceFromAudioBuffer(audioBuffer: AudioBuffer, context: AudioContext | OfflineAudioContext): AudioBufferSourceNode {
  const source = context.createBufferSource();
  source.buffer = audioBuffer;
  return source;
}

function round(value: number, decimals: number) {
  //@ts-ignore
  return Number(Math.round(value+'e'+decimals)+'e-'+decimals);
}
